import { Button, Grid, TextField } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import CategorySelect from '../ExecutiveCategory/Component/CategorySelect';
import GroupSelect from '../ExecutiveCategory/Component/GroupSelect';

type UpdateExecutiveProps = {
  handleClose: (val: boolean) => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  executive: any; // ideally use IExecutivePanel
};

type FormValues = {
  name: string;
  position: string;
  agency_designation: string;
  email: string;
  mobile: string;
  phone: string;
  website: string;
  agency_name: string;
  address: string;
  priority: string | number;
  category_id: string | number;
  group_id: string | number;
  files: FileList;
};

export default function UpdateExecutive({
  handleClose,
  setRefreshTrigger,
  executive,
}: UpdateExecutiveProps) {
  console.log(executive);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      name: executive?.name || '',
      position: executive?.position || '',
      agency_designation: executive?.agency_designation || '',
      email: executive?.email || '',
      mobile: executive?.mobile || '',
      phone: executive?.phone || '',
      website: executive?.website || '',
      agency_name: executive?.agency_name || '',
      address: executive?.address || '',
      priority: executive?.priority || '',
      category_id: executive?.category_id ?? '',
      group_id: executive?.group_id ?? '',
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  // If executive changes while modal open, sync form
  useEffect(() => {
    if (executive) {
      reset({
        name: executive.name || '',
        position: executive.position || '',
        agency_designation: executive.agency_designation || '',
        email: executive.email || '',
        mobile: executive.mobile || '',
        phone: executive.phone || '',
        website: executive.website || '',
        agency_name: executive.agency_name || '',
        address: executive.address || '',
        priority: executive.priority || '',
        category_id: executive.category_id ?? '',
        group_id: executive.group_id ?? '',
      });
    }
  }, [executive, reset]);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();
      // backend may not need id in body, but harmless if sent
      formData.append('id', String(executive.id));

      formData.append('name', values.name);
      formData.append('position', values.position);
      formData.append('agency_designation', values.agency_designation);
      formData.append('email', values.email);
      formData.append('mobile', values.mobile);
      formData.append('phone', values.phone);
      formData.append('website', values.website);
      formData.append('address', values.address);
      formData.append('agency_name', values.agency_name);
      formData.append('priority', String(values.priority || '0'));
      formData.append('category_id', String(values.category_id));
      formData.append('group_id', String(values.group_id));

      // only send new file if user selected one
      if (values.files && values.files[0]) {
        formData.append('files', values.files[0]);
      }

      const { data } = await axios.patch(
        `${url}/api/v1/admin/executive/${executive.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data?.success) {
        Toast.fire({
          icon: 'success',
          title: data?.message,
        });
        setRefreshTrigger((prev) => prev + 1);
        handleClose(false);
      } else {
        Toast.fire({
          icon: 'error',
          title: data?.message || 'Update failed',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: err?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{ p: 2, mt: 1 }}
    >
      <Grid container spacing={1} px={1}>
        {/* Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Enter a name'
            type='text'
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
        </Grid>

        {/* Position */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='position'
            label='Enter a designation'
            type='text'
            variant='outlined'
            error={!!errors.position}
            helperText={errors.position?.message}
            {...register('position', { required: 'Designation is required' })}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='email'
            label='Enter an Email'
            type='email'
            variant='outlined'
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Enter a valid email address',
              },
            })}
          />
        </Grid>

        {/* Mobile */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='mobile'
            label='Enter a Mobile number'
            type='tel'
            variant='outlined'
            error={!!errors.mobile}
            helperText={errors.mobile?.message}
            {...register('mobile', { required: 'Mobile number is required' })}
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='phone'
            label='Enter a Phone'
            type='tel'
            variant='outlined'
            error={!!errors.phone}
            helperText={errors.phone?.message}
            {...register('phone')}
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='address'
            label='Enter an Address'
            type='text'
            variant='outlined'
            error={!!errors.address}
            helperText={errors.address?.message}
            {...register('address')}
          />
        </Grid>

        {/* Website */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='website'
            label='Enter a website'
            type='url'
            variant='outlined'
            error={!!errors.website}
            helperText={errors.website?.message}
            {...register('website')}
          />
        </Grid>

        {/* Agency name */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='agency_name'
            label='Enter an Agency name'
            type='text'
            variant='outlined'
            error={!!errors.agency_name}
            helperText={errors.agency_name?.message}
            {...register('agency_name', {
              required: 'Agency name is required',
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='agency_designation'
            label='Enter an Agency Designation'
            type='text'
            variant='outlined'
            error={!!errors.agency_designation}
            helperText={errors.agency_designation?.message}
            {...register('agency_designation', {
              required: 'Agency Designation is required',
            })}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            label='Priority (1-5)'
            type='number'
            inputProps={{
              min: 0,
              step: '0.1',
            }}
            variant='outlined'
            error={!!errors.priority}
            helperText={errors.priority?.message}
            {...register('priority', {
              required: 'Priority is required',
              min: {
                value: 1,
                message: 'Priority must be at least 1',
              },
              max: {
                value: 5,
                message: 'Priority cannot be more than 5',
              },
              validate: (value) =>
                Number(value) >= 1 && Number(value) <= 5
                  ? true
                  : 'Priority must be between 1 and 5',
            })}
          />
        </Grid>

        {/* Category */}
        <Grid item xs={12} sm={6}>
          <CategorySelect<FormValues>
            register={register}
            errors={errors}
            defaultValue={executive?.category_id}
          />
        </Grid>

        {/* Group (Duration) */}
        <Grid item xs={12} sm={6}>
          <GroupSelect<FormValues>
            register={register}
            errors={errors}
            defaultValue={executive?.group_id}
          />
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12} sm={6}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload image'
            fieldName='files'
            hideCustomLabel
            onlyImage
            mode='single'
            imgSerUrl={executive?.image ? [executive.image] : []}
          />
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Executive'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
